import {
  Add,
  AddCommentOutlined,
  AttachFileOutlined,
  AutoStoriesOutlined,
  Build,
  ChatOutlined,
  LanguageOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import { MenuItem, Popover, styled, Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectWithLastScanned } from 'Types/dealGPT';

import ToolSettingsIcon from '../../assets/tool-settings.svg';
import IntellioAdvantageChatDrawer from '../../components/DealGPT/IntellioAdvantageChatDrawer';
import { useDrawer } from '../../components/Providers/DrawerProvider';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { usePromptDrawer } from '../../hooks/usePromptDrawer';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import {
  selectConversations,
  setCurrentConversation,
} from '../../redux/reducers/conversationReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { StyledIconButton } from '../../theme/CustomComponents';
import { AppEnum, DrawerTypes, IndexType, ToolName } from '../../Types/enums';
import { llms, ModelProviderEnum } from '../../Types/llm';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';
import { useFeatures } from '../Providers/FeatureProvider';

interface ChatMenuProps {
  onAttachDocuments: () => void;
  onAttachTemporaryFiles: (files: File[]) => void;
  onOtherTool: (toolName: ToolName) => void;
  onDeepResearch: () => void;
  onToolSettings: () => void;
  disabled?: boolean;
  isTemporary?: boolean;
  disabledOptions?: ToolName[];
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  gap: theme.spacing(2),
}));

const ChatMenu: React.FC<ChatMenuProps> = ({
  onAttachDocuments,
  onAttachTemporaryFiles,
  onOtherTool,
  onDeepResearch,
  onToolSettings,
  disabled,
  isTemporary = false,
  disabledOptions = [],
}: ChatMenuProps) => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();
  const features = useFeatures();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { currentUser } = useAuth();
  const { selectedApp, llm } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const { currentConversation } = useSelector(selectConversations);
  const { openPromptDrawer } = usePromptDrawer({ drawerType: DrawerTypes.PROMPT_LIST });
  const { openDrawer } = useDrawer();

  const isIAChat = selectedApp === AppEnum.DEAL_GPT;
  const isGraphRagProject = currentFolder?.project?.indexToUse === IndexType.GRAPH_RAG;

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNewchat = useCallback(() => {
    const config = isIAChat
      ? {
          event: AnalyticsEvent.WM_IA_CHAT_NEW,
          id: currentFolder?.projectId,
          metadata: {
            projectId: currentFolder?.projectId,
          },
        }
      : {
          event: AnalyticsEvent.WM_NIGEL_NEW_CHAT,
          id: currentFolder?.id,
          metadata: {
            folderId: currentFolder?.id,
          },
        };

    analytics.event(config.event, { id: config.id }, config.metadata);
    navigate(`/folder/${currentFolder?.id}`);
    dispatch(setCurrentConversation(undefined));
  }, [isIAChat, currentFolder, dispatch, navigate, analytics]);

  const handleAttachDocuments = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_CHAT_ATTACH_DOCUMENTS);
    handleClose();
    onAttachDocuments();
  }, [analytics, handleClose, onAttachDocuments]);

  const handleTemporaryFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      const files = Array.from(e.target.files);

      // Clear target value to allow the same filename to be uploaded in succession
      e.target.value = '';

      analytics.event(AnalyticsEvent.WM_NIGEL_CHAT_ATTACH_DOCUMENTS);
      handleClose();
      onAttachTemporaryFiles(files);
    },
    [analytics, handleClose, onAttachTemporaryFiles]
  );

  const handleToolsUpdate = useCallback(() => {
    handleClose();
    onToolSettings();
  }, [handleClose, onToolSettings]);

  const handleLibraryOpen = useCallback(() => {
    openPromptDrawer();
    handleClose();
  }, [openPromptDrawer, handleClose]);

  const handleChatDrawerOpen = useCallback(() => {
    if (isGraphRagProject) return;

    handleClose();
    openDrawer({
      anchor: 'right',
      header: 'Focused Chat',
      content: (
        <IntellioAdvantageChatDrawer
          project={currentFolder?.project as ProjectWithLastScanned}
          redirectOnProcess
        />
      ),
    });
  }, [currentFolder, isGraphRagProject, openDrawer, handleClose]);

  return (
    <>
      <StyledIconButton disabled={disabled} onClick={handleOpen} sx={{ color: '#070154' }}>
        <Add />
      </StyledIconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 200, borderRadius: 3 },
          },
        }}
      >
        {!!currentConversation && (
          <StyledMenuItem onClick={handleNewchat}>
            <AddCommentOutlined sx={{ height: '20px', width: '20px' }} />
            New Chat
          </StyledMenuItem>
        )}
        {isTemporary ? (
          <StyledMenuItem sx={{ cursor: 'pointer', padding: 0 }}>
            <label
              htmlFor="chat-menu-file-upload"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '6px 16px',
                width: '100%',
                cursor: 'pointer',
              }}
            >
              <input
                id="chat-menu-file-upload"
                type="file"
                hidden
                multiple
                onChange={handleTemporaryFileInput}
              />
              <AttachFileOutlined sx={{ height: '20px', width: '20px' }} />
              Attach Files
            </label>
          </StyledMenuItem>
        ) : (
          <StyledMenuItem onClick={handleAttachDocuments}>
            <AttachFileOutlined sx={{ height: '20px', width: '20px' }} />
            Attach Files
          </StyledMenuItem>
        )}
        {selectedApp === AppEnum.DEAL_GPT && !isGraphRagProject && (
          <StyledMenuItem onClick={handleChatDrawerOpen}>
            <ChatOutlined sx={{ height: '20px', width: '20px' }} />
            Focused Chat
          </StyledMenuItem>
        )}
        <StyledMenuItem
          onClick={() => {
            handleClose();
            onOtherTool(ToolName.WEB_SEARCH);
          }}
        >
          <LanguageOutlined sx={{ height: '20px', width: '20px' }} />
          Web Search
        </StyledMenuItem>
        <StyledMenuItem
          onClick={() => {
            handleClose();
            onOtherTool(ToolName.GLEAN_SEARCH);
          }}
        >
          <LanguageOutlined sx={{ height: '20px', width: '20px' }} />
          Glean Search
        </StyledMenuItem>
        {!isTemporary && (
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onDeepResearch();
            }}
            disabled={llms[llm].modelProvider !== ModelProviderEnum.OPENAI}
          >
            <ScienceOutlined sx={{ height: '20px', width: '20px' }} />
            Deep Research <br /> ({currentUser?.researchCredits}{' '}
            {(currentUser?.researchCredits || 0) === 1 ? 'credit' : 'credits'} remaining)
          </StyledMenuItem>
        )}

        {features.stracl && (
          <Tooltip
            placement="top"
            title={
              disabledOptions?.includes(ToolName.STRACL_MCP)
                ? 'Deep Research does not support the use of Stracl MCP'
                : ''
            }
          >
            <div>
              <StyledMenuItem
                disabled={disabledOptions?.includes(ToolName.STRACL_MCP)}
                onClick={() => {
                  handleClose();
                  onOtherTool(ToolName.STRACL_MCP);
                }}
              >
                <Build sx={{ height: '20px', width: '20px' }} />
                Stracl MCP
              </StyledMenuItem>
            </div>
          </Tooltip>
        )}
        <StyledMenuItem onClick={handleLibraryOpen}>
          <AutoStoriesOutlined sx={{ height: '20px', width: '20px' }} />
          Library
        </StyledMenuItem>
        {features.show_tools && !isIAChat && (
          <StyledMenuItem onClick={handleToolsUpdate}>
            <img
              src={ToolSettingsIcon}
              alt="Enable Tools"
              style={{ height: '20px', width: '20px' }}
            />
            Tools
          </StyledMenuItem>
        )}
      </Popover>
    </>
  );
};

export default ChatMenu;
